import React, { useState, useCallback } from 'react';
import {
    useDataProvider,
    useNotify,
    useRefresh,
    FormDataConsumer,
    ReferenceInput,
    SelectInput,
    DateInput,
    TextInput,
} from 'react-admin';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Typography,
    Chip,
    Box,
    Paper,
    CircularProgress,
} from '@material-ui/core';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    GetApp as DownloadIcon,
    Visibility as ViewIcon,
    CloudUpload as UploadIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

interface Document {
    id: number;
    documentType: string;
    documentNumber: string;
    expiryDate: string;
    description: string;
    file: File | null;
    fileData: string | null;
    fileName: string;
    isVerified: boolean;
}

const useStyles = makeStyles(theme => ({
    addButton: {
        marginBottom: theme.spacing(2),
    },
    table: {
        marginTop: theme.spacing(2),
    },
    fileInput: {
        display: 'none',
    },
    uploadArea: {
        border: '2px dashed #ccc',
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(4),
        textAlign: 'center',
        cursor: 'pointer',
        marginBottom: theme.spacing(2),
        '&:hover': {
            borderColor: theme.palette.primary.main,
        },
    },
    documentRow: {
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    verifiedChip: {
        backgroundColor: theme.palette.success.main,
        color: 'white',
    },
    pendingChip: {
        backgroundColor: theme.palette.warning.main,
        color: 'white',
    },
}));

const DocumentUpload = ({ onCreate = false }) => {
    const classes = useStyles();
    const [documents, setDocuments] = useState<Document[]>([]);
    const [documentTypes, setDocumentTypes] = useState<any[]>([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingDocument, setEditingDocument] = useState<Document | null>(
        null
    );
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState<{
        documentType: string;
        documentNumber: string;
        expiryDate: string;
        description: string;
        file: File | null;
        fileData: string | null;
    }>({
        documentType: '',
        documentNumber: '',
        expiryDate: '',
        description: '',
        file: null,
        fileData: null,
    });
    const notify = useNotify();
    const dataProvider = useDataProvider();

    // Fetch document types from backend
    React.useEffect(() => {
        const fetchDocumentTypes = async () => {
            try {
                const { data } = await dataProvider.getList('document-types', {
                    pagination: { page: 1, perPage: 100 },
                    sort: { field: 'name', order: 'ASC' },
                    filter: { isActive: true },
                });
                setDocumentTypes(data);
            } catch (error) {
                console.error('Error fetching document types:', error);
                notify('Error loading document types', 'warning');
            }
        };

        fetchDocumentTypes();
    }, [dataProvider, notify]);

    const handleAddDocument = () => {
        setEditingDocument(null);
        setFormData({
            documentType: '',
            documentNumber: '',
            expiryDate: '',
            description: '',
            file: null,
            fileData: null,
        });
        setOpenDialog(true);
    };

    const handleEditDocument = doc => {
        setEditingDocument(doc);
        setFormData({
            documentType: doc.documentType,
            documentNumber: doc.documentNumber,
            expiryDate: doc.expiryDate,
            description: doc.description,
            file: doc.file,
            fileData: doc.fileData,
        });
        setOpenDialog(true);
    };

    const handleDeleteDocument = index => {
        const newDocuments = documents.filter((_, i) => i !== index);
        setDocuments(newDocuments);
        notify('Document removed', 'info');
    };

    const handleFileChange = event => {
        const file = event.target.files[0];
        if (file) {
            if (file.type !== 'application/pdf') {
                notify('Only PDF files are allowed', 'warning');
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                notify('File size must be less than 5MB', 'warning');
                return;
            }

            // Convert file to base64
            const reader = new FileReader();
            reader.onload = e => {
                const base64Data = e.target?.result as string;
                const base64Content = base64Data.split(',')[1]; // Remove data URL prefix
                setFormData({
                    ...formData,
                    file,
                    fileData: base64Content,
                });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSaveDocument = async () => {
        // Prevent multiple clicks
        if (saving) {
            return;
        }

        if (!formData.documentType) {
            notify('ra.validation.required', 'error', {
                field: 'Document type',
            });
            return;
        }

        setSaving(true);

        // Simulate async operation for better UX
        await new Promise(resolve => setTimeout(resolve, 500));

        try {
            const newDoc = {
                id: editingDocument ? editingDocument.id : Date.now(),
                documentType: formData.documentType,
                documentNumber: formData.documentNumber,
                expiryDate: formData.expiryDate,
                description: formData.description,
                file: formData.file,
                fileData: formData.fileData,
                fileName: formData.file
                    ? formData.file.name
                    : editingDocument
                    ? editingDocument.fileName
                    : '',
                isVerified: false,
            };

            if (editingDocument) {
                const newDocuments = documents.map(doc =>
                    doc.id === editingDocument.id ? newDoc : doc
                );
                setDocuments(newDocuments);
                notify('Document updated successfully', 'success');
            } else {
                setDocuments([...documents, newDoc]);
                notify('Document added successfully', 'success');
            }

            setOpenDialog(false);
        } finally {
            setSaving(false);
        }
    };

    const handleViewFile = doc => {
        if (doc.file && doc.fileData) {
            // Create blob from base64 data for viewing
            try {
                const byteCharacters = atob(doc.fileData);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank');
                URL.revokeObjectURL(url); // Clean up
            } catch (error) {
                notify('Error viewing file', 'error');
            }
        } else if (doc.file) {
            // Fallback for file object (shouldn't happen in create flow)
            const url = URL.createObjectURL(doc.file);
            window.open(url, '_blank');
        }
    };

    const handleDownloadFile = doc => {
        if (doc.file && doc.fileData) {
            // Create blob from base64 data for download
            try {
                const byteCharacters = atob(doc.fileData);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = doc.fileName || 'document.pdf';
                a.click();
                URL.revokeObjectURL(url); // Clean up
            } catch (error) {
                notify('Error downloading file', 'error');
            }
        } else if (doc.file) {
            // Fallback for file object (shouldn't happen in create flow)
            const url = URL.createObjectURL(doc.file);
            const a = document.createElement('a');
            a.href = url;
            a.download = doc.fileName || 'document.pdf';
            a.click();
            URL.revokeObjectURL(url);
        }
    };

    return (
        <Box>
            <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddDocument}
                className={classes.addButton}
            >
                Add Document
            </Button>

            {documents.length === 0 ? (
                <Paper className={classes.uploadArea}>
                    <Typography variant="h6" gutterBottom>
                        No documents added yet
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Click "Add Document" to upload documents for this user
                    </Typography>
                </Paper>
            ) : (
                <Table className={classes.table}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Document Type</TableCell>
                            <TableCell>Document Number</TableCell>
                            <TableCell>Expiry Date</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>File</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {documents.map((doc, index) => (
                            <TableRow
                                key={doc.id}
                                className={classes.documentRow}
                            >
                                <TableCell>{doc.documentType}</TableCell>
                                <TableCell>{doc.documentNumber}</TableCell>
                                <TableCell>{doc.expiryDate}</TableCell>
                                <TableCell>{doc.description}</TableCell>
                                <TableCell>
                                    {doc.fileName ? (
                                        <Typography
                                            variant="body2"
                                            color="primary"
                                        >
                                            {doc.fileName}
                                        </Typography>
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                        >
                                            No file uploaded
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label="Pending"
                                        size="small"
                                        className={classes.pendingChip}
                                    />
                                </TableCell>
                                <TableCell>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleEditDocument(doc)}
                                        title="Edit"
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    {doc.file && (
                                        <>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleViewFile(doc)
                                                }
                                                title="View"
                                            >
                                                <ViewIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleDownloadFile(doc)
                                                }
                                                title="Download"
                                            >
                                                <DownloadIcon />
                                            </IconButton>
                                        </>
                                    )}
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            handleDeleteDocument(index)
                                        }
                                        title="Delete"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            {/* Hidden form fields to store document data for form submission */}
            {documents.map((doc, index) => (
                <FormDataConsumer key={`doc-${index}`}>
                    {({ formData, ...rest }) => {
                        // Initialize documents array in form data if not exists
                        if (!formData.documents) {
                            formData.documents = [];
                        }

                        // Update form data with current document
                        const documentData = {
                            documentType: doc.documentType,
                            documentNumber: doc.documentNumber,
                            expiryDate: doc.expiryDate,
                            description: doc.description,
                            fileName: doc.fileName,
                            fileData: doc.file ? doc.fileData : null,
                        };

                        formData.documents[index] = documentData;

                        return null;
                    }}
                </FormDataConsumer>
            ))}

            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {editingDocument ? 'Edit Document' : 'Add Document'}
                </DialogTitle>
                <DialogContent>
                    <FormControl fullWidth style={{ marginBottom: 16 }}>
                        <InputLabel>Document Type *</InputLabel>
                        <Select
                            value={formData.documentType}
                            onChange={(e: any) =>
                                setFormData({
                                    ...formData,
                                    documentType: e.target.value,
                                })
                            }
                            required
                        >
                            <MenuItem value="">Select Document Type</MenuItem>
                            {documentTypes.map(type => (
                                <MenuItem key={type.id} value={type.name}>
                                    {type.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Document Number"
                        fullWidth
                        value={formData.documentNumber}
                        onChange={e =>
                            setFormData({
                                ...formData,
                                documentNumber: e.target.value,
                            })
                        }
                        style={{ marginBottom: 16 }}
                    />
                    <TextField
                        label="Expiry Date"
                        type="date"
                        fullWidth
                        value={formData.expiryDate}
                        onChange={e =>
                            setFormData({
                                ...formData,
                                expiryDate: e.target.value,
                            })
                        }
                        InputLabelProps={{
                            shrink: true,
                        }}
                        style={{ marginBottom: 16 }}
                    />
                    <TextField
                        label="Description"
                        multiline
                        rows={3}
                        fullWidth
                        value={formData.description}
                        onChange={e =>
                            setFormData({
                                ...formData,
                                description: e.target.value,
                            })
                        }
                        style={{ marginBottom: 16 }}
                    />

                    <Box mt={2}>
                        <input
                            accept="application/pdf"
                            className={classes.fileInput}
                            id="document-file-upload"
                            type="file"
                            onChange={handleFileChange}
                        />
                        <label htmlFor="document-file-upload">
                            <Button
                                variant="outlined"
                                component="span"
                                startIcon={<UploadIcon />}
                                fullWidth
                            >
                                {formData.file
                                    ? formData.file.name
                                    : 'Choose PDF File'}
                            </Button>
                        </label>
                        {formData.file && (
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{ marginTop: 8 }}
                            >
                                File size:{' '}
                                {(formData.file.size / 1024 / 1024).toFixed(2)}{' '}
                                MB
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setOpenDialog(false)}
                        color="default"
                        disabled={saving}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveDocument}
                        color="primary"
                        variant="contained"
                        disabled={saving}
                        startIcon={
                            saving ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : null
                        }
                        style={{
                            minWidth: '120px',
                            opacity: saving ? 0.7 : 1,
                        }}
                    >
                        {saving
                            ? 'Saving...'
                            : editingDocument
                            ? 'Update'
                            : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DocumentUpload;
